(function (root, factory) {
  "use strict";

  const api = factory();

  if (typeof module !== "undefined" && module.exports) {
    module.exports = api;
  }

  if (root && root.document) {
    root.VinlightingProductPdf = api;
    api.boot(root);
  }
})(typeof window !== "undefined" ? window : globalThis, function () {
  "use strict";

  const PDF_CONFIG = Object.freeze({
    buttonId: "download-pdf-1",
    productDataId: "product-data",
    logoUrl:
      "https://cdn.shopify.com/s/files/1/0634/9317/7544/files/Vinlighting-Primary-Logo.png?v=1787305830",
    certificateUrl:
      "https://cdn.shopify.com/s/files/1/0634/9317/7544/files/certificate.png",
    email: "support@vinlighting.com",
    phone: "+86 180 1121 8638",
    addressLines: Object.freeze([
      "Oriental Pearl Commercial Building, 30 Xinxing Middle Road",
      "Guzhen Town, Zhongshan, Guangdong 528421, China",
    ]),
    availability: "In Stock",
    shippingOptions: Object.freeze([
      "Standard Shipping: 4–6 Weeks",
      "Expedited Shipping: 2–4 Weeks",
    ]),
    imageTimeoutMs: 10000,
    imageConcurrency: 3,
    selectors: Object.freeze({
      collapsibleWrapper:
        ".collapsibles-wrapper.collapsibles-wrapper--border-bottom",
      collapsibleContent: ".collapsible-content__inner.rte",
      variantContainers: Object.freeze([
        ".product-form__input",
        "fieldset.product-form__input",
        ".product-form__option",
        ".selector-wrapper",
        "variant-selects",
        "variant-radios",
      ]),
      variantLabel:
        ".variant__label, .product-form__option-name, legend, label",
    }),
    page: Object.freeze({
      leftColumnX: 15,
      rightColumnX: 115,
      columnWidth: 85,
      contentBottom: 272,
      continuationTopOffset: 3,
    }),
    sectionSpacing: Object.freeze({
      descriptionToDetails: 8,
      keyFeaturesToSpecifications: 4,
      productOptionsToDimensions: 8,
    }),
  });

  const initializedButtons = new WeakSet();
  let fontScriptPromise = null;

  function cleanText(value) {
    if (value === null || value === undefined) return "";

    return String(value)
      .replace(/\$(?=[^$\r\n]*\\[A-Za-z]+)([^$\r\n]+)\$/g, "$1")
      .replace(/\\times/g, " × ")
      .replace(/\\sim/g, "~")
      .replace(/\\circ/g, "°")
      .replace(/\^\{\\prime\\prime\}/g, '"')
      .replace(/\^\{\\prime\}/g, "'")
      .replace(/[{}]/g, "")
      .replace(/\s+/g, " ")
      .trim();
  }

  function splitIntoSentences(
    value,
    environment = typeof globalThis !== "undefined" ? globalThis : {}
  ) {
    const text = cleanText(value);
    if (!text) return [];

    const intl = environment && environment.Intl;
    if (intl && typeof intl.Segmenter === "function") {
      try {
        const segmenter = new intl.Segmenter("en-US", {
          granularity: "sentence",
        });
        return Array.from(segmenter.segment(text), (segment) =>
          cleanText(segment.segment)
        ).filter(Boolean);
      } catch (error) {
        // Use the punctuation fallback when sentence segmentation is unavailable.
      }
    }

    const sentences = [];
    let start = 0;
    for (let index = 0; index < text.length; index += 1) {
      const character = text[index];
      if (!/[.!?]/.test(character)) continue;
      const previous = text[index - 1];
      const next = text[index + 1];
      const isDecimalPoint =
        character === "." && /\d/.test(previous || "") && /\d/.test(next || "");
      const endsSentence = !next || /\s/.test(next);
      if (isDecimalPoint || !endsSentence) continue;

      const sentence = cleanText(text.slice(start, index + 1));
      if (sentence) sentences.push(sentence);
      start = index + 1;
    }

    const remainder = cleanText(text.slice(start));
    if (remainder) sentences.push(remainder);
    return sentences;
  }

  function sanitizeFileName(value, maxLength = 120) {
    const cleaned = cleanText(value)
      .replace(/[<>:"/\\|?*\u0000-\u001f]+/g, "-")
      .slice(0, maxLength)
      .trim()
      .replace(/[. ]+$/g, "");

    return cleaned || "Vinlighting Product";
  }

  function fitWithin(width, height, maxWidth, maxHeight) {
    if (
      ![width, height, maxWidth, maxHeight].every(
        (number) => Number.isFinite(number) && number > 0
      )
    ) {
      throw new Error("Image dimensions must be positive numbers.");
    }

    const scale = Math.min(1, maxWidth / width, maxHeight / height);
    return {
      width: Math.round(width * scale * 100) / 100,
      height: Math.round(height * scale * 100) / 100,
    };
  }

  function withTimeout(promise, timeoutMs, message) {
    let timer;
    return Promise.race([
      promise,
      new Promise((resolve, reject) => {
        timer = setTimeout(() => reject(new Error(message)), timeoutMs);
      }),
    ]).finally(() => clearTimeout(timer));
  }

  async function mapWithConcurrency(items, limit, worker) {
    const results = new Array(items.length);
    let nextIndex = 0;

    async function runWorker() {
      while (nextIndex < items.length) {
        const index = nextIndex;
        nextIndex += 1;
        results[index] = await worker(items[index], index);
      }
    }

    const workerCount = Math.min(Math.max(1, limit), items.length);
    await Promise.all(Array.from({ length: workerCount }, runWorker));
    return results;
  }

  function getShopifyImageUrl(imageUrl, width) {
    try {
      const url = new URL(imageUrl);
      if (url.hostname !== "cdn.shopify.com") return imageUrl;
      url.searchParams.set("width", String(width));
      return url.toString();
    } catch (error) {
      return imageUrl;
    }
  }

  function loadImageElement(imageUrl, options) {
    const { ImageCtor, timeoutMs } = options;
    const operation = new Promise((resolve, reject) => {
      const image = new ImageCtor();
      image.crossOrigin = "anonymous";
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error(`Image load failed: ${imageUrl}`));
      image.src = imageUrl;
    });

    return withTimeout(operation, timeoutMs, `Image load timed out: ${imageUrl}`);
  }

  async function convertImageToDataUrl(imageUrl, options = {}, environment = {}) {
    if (typeof imageUrl !== "string" || imageUrl.trim() === "") {
      throw new Error("Image URL is unavailable.");
    }

    const ImageCtor = environment.ImageCtor ||
      (typeof Image !== "undefined" ? Image : null);
    const documentRef = environment.documentRef ||
      (typeof document !== "undefined" ? document : null);

    if (!ImageCtor || !documentRef) {
      throw new Error("Browser image APIs are unavailable.");
    }

    const keepPng = Boolean(options.keepPng);
    const maxWidth = options.maxWidth || 800;
    const maxHeight = options.maxHeight || 1200;
    const timeoutMs = options.timeoutMs || PDF_CONFIG.imageTimeoutMs;
    const optimizedUrl = getShopifyImageUrl(imageUrl, maxWidth);
    const candidates = optimizedUrl === imageUrl
      ? [imageUrl]
      : [optimizedUrl, imageUrl];

    let image;
    let lastError;
    for (const candidate of candidates) {
      try {
        image = await loadImageElement(candidate, { ImageCtor, timeoutMs });
        break;
      } catch (error) {
        lastError = error;
      }
    }

    if (!image) throw lastError || new Error("Image load failed.");

    const sourceWidth = image.naturalWidth || image.width;
    const sourceHeight = image.naturalHeight || image.height;
    const fitted = fitWithin(sourceWidth, sourceHeight, maxWidth, maxHeight);
    const canvas = documentRef.createElement("canvas");
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Canvas rendering is unavailable.");

    canvas.width = Math.max(1, Math.round(fitted.width));
    canvas.height = Math.max(1, Math.round(fitted.height));

    if (!keepPng) {
      context.fillStyle = "#FFFFFF";
      context.fillRect(0, 0, canvas.width, canvas.height);
    }
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    const mimeType = keepPng ? "image/png" : "image/jpeg";
    return {
      dataUrl: keepPng
        ? canvas.toDataURL(mimeType)
        : canvas.toDataURL(mimeType, 0.72),
      format: keepPng ? "PNG" : "JPEG",
      width: canvas.width,
      height: canvas.height,
    };
  }

  function isPlaceholderOption(value) {
    return /^(?:[-–—]+\s*)?(?:please\s+)?(?:select|choose)\b/i.test(
      cleanText(value)
    );
  }

  function mergeVariantGroups(groups) {
    const merged = new Map();

    groups.forEach((group) => {
      const subheading = cleanText(group && group.subheading);
      if (!subheading) return;

      if (!merged.has(subheading)) merged.set(subheading, new Set());
      const optionSet = merged.get(subheading);
      (group.options || []).forEach((value) => {
        const option = cleanText(value);
        if (option && !isPlaceholderOption(option)) optionSet.add(option);
      });
    });

    return Array.from(merged, ([subheading, optionSet]) => ({
      subheading,
      options: Array.from(optionSet),
    })).filter((group) => group.options.length > 0);
  }

  function readProductJson(documentRef) {
    const element = documentRef.getElementById(PDF_CONFIG.productDataId);
    if (!element) throw new Error("Product data is unavailable.");

    const json = element.getAttribute("data-product");
    if (!json) throw new Error("Product data is unavailable.");

    let product;
    try {
      product = JSON.parse(json);
    } catch (error) {
      throw new Error("Product data is invalid.");
    }

    if (!product || typeof product.title !== "string" || !product.title.trim()) {
      throw new Error("Product data is invalid.");
    }
    return product;
  }

  function findCollapsibleSection(documentRef, label) {
    const target = cleanText(label).toLowerCase();
    const wrappers = documentRef.querySelectorAll(
      PDF_CONFIG.selectors.collapsibleWrapper
    );

    for (const wrapper of wrappers) {
      const button = wrapper.querySelector("button");
      if (
        button &&
        cleanText(button.textContent).toLowerCase().includes(target)
      ) {
        return wrapper.querySelector(PDF_CONFIG.selectors.collapsibleContent);
      }
    }
    return null;
  }

  function readRichSection(sectionElement) {
    if (!sectionElement) {
      return { paragraphs: [], lists: [], images: [] };
    }

    const paragraphs = Array.from(sectionElement.querySelectorAll("p"))
      .filter((paragraph) =>
        typeof paragraph.closest === "function"
          ? !paragraph.closest("li, table, dl")
          : true
      )
      .map((paragraph) => cleanText(paragraph.textContent))
      .filter(Boolean);
    const paragraphSet = new Set(paragraphs);
    const addParagraph = (value) => {
      const text = cleanText(value);
      if (text && !paragraphSet.has(text)) {
        paragraphSet.add(text);
        paragraphs.push(text);
      }
    };

    const lists = Array.from(sectionElement.querySelectorAll("ul, ol"))
      .map((list) => ({
        items: Array.from(list.querySelectorAll("li"))
          .map((item) => cleanText(item.innerText || item.textContent))
          .filter(Boolean),
      }))
      .filter((list) => list.items.length > 0);

    Array.from(sectionElement.querySelectorAll("table tr")).forEach((row) => {
      const cells = Array.from(row.querySelectorAll("th, td"))
        .map((cell) => cleanText(cell.textContent))
        .filter(Boolean);
      if (cells.length === 2) {
        const separator = cells[0].endsWith(":") ? " " : ": ";
        addParagraph(`${cells[0]}${separator}${cells[1]}`);
      } else if (cells.length > 0) {
        addParagraph(cells.join(" | "));
      }
    });

    Array.from(sectionElement.querySelectorAll("dl")).forEach((list) => {
      Array.from(list.querySelectorAll("dt")).forEach((term) => {
        const description = term.nextElementSibling;
        if (
          description &&
          String(description.tagName || "").toUpperCase() === "DD"
        ) {
          const label = cleanText(term.textContent);
          const value = cleanText(description.textContent);
          if (label && value) addParagraph(`${label}: ${value}`);
          else addParagraph(label || value);
        } else {
          addParagraph(term.textContent);
        }
      });
    });

    Array.from(
      sectionElement.querySelectorAll("div, h2, h3, h4, h5, h6")
    )
      .filter((block) => {
        const containsStructuredContent =
          typeof block.querySelector === "function" &&
          block.querySelector("p, ul, ol, table, dl, div, h2, h3, h4, h5, h6");
        const isInsideStructuredContent =
          typeof block.closest === "function" &&
          block.closest("li, table, dl");
        return !containsStructuredContent && !isInsideStructuredContent;
      })
      .forEach((block) => addParagraph(block.textContent));

    if (paragraphs.length === 0 && lists.length === 0) {
      const fallback = cleanText(
        sectionElement.innerText || sectionElement.textContent
      );
      if (fallback) paragraphs.push(fallback);
    }

    const images = Array.from(sectionElement.querySelectorAll("img"))
      .map((image) => image.currentSrc || image.src)
      .filter(Boolean);

    return { paragraphs, lists, images };
  }

  function extractVariantGroups(documentRef) {
    const selector = PDF_CONFIG.selectors.variantContainers.join(", ");
    const matchedContainers = Array.from(
      new Set(documentRef.querySelectorAll(selector))
    );
    const containers = matchedContainers.filter(
      (candidate) =>
        !matchedContainers.some(
          (other) =>
            other !== candidate &&
            typeof candidate.contains === "function" &&
            candidate.contains(other)
        )
    );
    const groups = [];

    containers.forEach((container) => {
      const label = container.querySelector(PDF_CONFIG.selectors.variantLabel);
      if (!label) return;

      const subheading = cleanText(label.textContent);
      const options = Array.from(
        container.querySelectorAll("option, input[type=radio]")
      )
        .map((option) => {
          if (option.tagName === "OPTION") return option.textContent;
          return option.nextElementSibling
            ? option.nextElementSibling.textContent
            : option.value;
        })
        .map(cleanText)
        .filter((option) => !isInvalidProductOption(option));

      if (!isInvalidProductOption(subheading)) {
        groups.push({ subheading, options });
      }
    });

    return mergeVariantGroups(groups);
  }

  function getProductOptionValue(value) {
    if (typeof value === "string" || typeof value === "number") {
      return cleanText(value);
    }
    if (!value || typeof value !== "object") return "";
    return cleanText(value.value ?? value.title ?? value.name);
  }

  function getProductOptionDefinitionName(definition) {
    if (!definition || typeof definition !== "object") {
      return cleanText(definition);
    }
    return cleanText(definition.name ?? definition.title);
  }

  function isInvalidProductOption(value) {
    const option = cleanText(value);
    return (
      !option ||
      /^default title$/i.test(option) ||
      isPlaceholderOption(option)
    );
  }

  function extractVariantGroupsFromProduct(product) {
    if (!product || !Array.isArray(product.options)) return [];

    const variants = Array.isArray(product.variants) ? product.variants : [];
    const groups = product.options.map((definition, index) => {
      const isObjectDefinition =
        definition && typeof definition === "object";
      const subheading = isObjectDefinition
        ? getProductOptionDefinitionName(definition)
        : cleanText(definition);
      if (isInvalidProductOption(subheading)) {
        return null;
      }

      const directValues = isObjectDefinition && Array.isArray(definition.values)
          ? definition.values
            .map(getProductOptionValue)
            .filter((value) => !isInvalidProductOption(value))
        : [];
      const values = directValues.length > 0
        ? directValues
        : variants
            .map((variant) => {
              if (!variant || typeof variant !== "object") return "";

              const optionFromArray = Array.isArray(variant.options)
                ? getProductOptionValue(variant.options[index])
                : "";
              if (!isInvalidProductOption(optionFromArray)) {
                return optionFromArray;
              }

              return getProductOptionValue(variant[`option${index + 1}`]);
            })
            .filter((value) => !isInvalidProductOption(value));

      return { subheading, options: values };
    });

    return mergeVariantGroups(groups.filter(Boolean));
  }

  function getVariantGroups(documentRef, product) {
    const domGroups = extractVariantGroups(documentRef);
    return domGroups.length > 0
      ? domGroups
      : extractVariantGroupsFromProduct(product);
  }

  function normalizeImageUrl(image) {
    if (typeof image === "string") return image;
    if (!image || typeof image !== "object") return "";
    return image.src || image.url || "";
  }

  function collectProductSnapshot(documentRef) {
    const product = readProductJson(documentRef);
    const readSection = (label) =>
      readRichSection(findCollapsibleSection(documentRef, label));

    return {
      product,
      title: cleanText(product.title),
      images: Array.from(product.images || [])
        .map(normalizeImageUrl)
        .filter(Boolean),
      description: readSection("description"),
      keyFeatures: readSection("key features"),
      specifications: readSection("specifications"),
      dimensions: readSection("dimensions"),
      variants: getVariantGroups(documentRef, product),
    };
  }

  class PageManager {
    constructor(pdf, options) {
      this.pdf = pdf;
      this.contentBottom = options.contentBottom;
      this.decoratePage = options.decoratePage;
      this.drawFooter = options.drawFooter;
      this.contentTops = new Map();
    }

    initializeFirstPage() {
      if (!this.contentTops.has(1)) {
        this.pdf.setPage(1);
        this.contentTops.set(1, this.decoratePage(1));
      }
      return this.getContentTop(1);
    }

    ensurePage(pageNumber) {
      while (this.pdf.internal.getNumberOfPages() < pageNumber) {
        this.pdf.addPage();
        const createdPage = this.pdf.internal.getNumberOfPages();
        this.contentTops.set(createdPage, this.decoratePage(createdPage));
      }
      this.activate(pageNumber);
      return this.getContentTop(pageNumber);
    }

    activate(pageNumber) {
      this.pdf.setPage(pageNumber);
    }

    getContentTop(pageNumber) {
      if (!this.contentTops.has(pageNumber)) {
        throw new Error(`Page ${pageNumber} has not been initialized.`);
      }
      return this.contentTops.get(pageNumber);
    }

    getPageCount() {
      return this.pdf.internal.getNumberOfPages();
    }

    finalizeFooters() {
      const pageCount = this.getPageCount();
      for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
        this.activate(pageNumber);
        this.drawFooter(pageNumber);
      }
      this.activate(pageCount);
    }
  }

  class ColumnCursor {
    constructor(pageManager, options) {
      this.pageManager = pageManager;
      this.x = options.x;
      this.maxWidth = options.maxWidth;
      this.pageNumber = options.pageNumber;
      this.y = options.y;
    }

    ensureSpace(height) {
      const contentTop = this.pageManager.getContentTop(this.pageNumber);
      const fullBodyHeight = this.pageManager.contentBottom - contentTop;
      if (!Number.isFinite(height) || height < 0 || height > fullBodyHeight) {
        throw new Error("Content exceeds the available page height.");
      }

      this.pageManager.activate(this.pageNumber);
      if (this.y + height <= this.pageManager.contentBottom) return false;

      this.pageNumber += 1;
      this.y = this.pageManager.ensurePage(this.pageNumber);
      return true;
    }
  }

  class FontManager {
    constructor(rootRef) {
      this.rootRef = rootRef || {};
      this.customFontsReady = false;
    }

    register(pdf) {
      const playfair = this.rootRef.PlayfairDisplayRegularBase64;
      const lato = this.rootRef.LatoRegularBase64;
      if (!playfair || !lato) {
        this.customFontsReady = false;
        return false;
      }

      try {
        pdf.addFileToVFS("PlayfairDisplay-Regular.ttf", playfair);
        pdf.addFont(
          "PlayfairDisplay-Regular.ttf",
          "PlayfairDisplay-Regular",
          "normal"
        );
        pdf.addFileToVFS("Lato-Regular.ttf", lato);
        pdf.addFont("Lato-Regular.ttf", "Lato-Regular", "normal");
        this.customFontsReady = true;
        return true;
      } catch (error) {
        this.customFontsReady = false;
        return false;
      }
    }

    setTitle(pdf) {
      pdf.setFont(
        this.customFontsReady ? "PlayfairDisplay-Regular" : "times",
        "normal"
      );
      pdf.setFontSize(14);
    }

    setSubheading(pdf) {
      pdf.setFont(
        this.customFontsReady ? "PlayfairDisplay-Regular" : "times",
        "normal"
      );
      pdf.setFontSize(12);
    }

    setBody(pdf) {
      pdf.setFont(this.customFontsReady ? "Lato-Regular" : "helvetica", "normal");
      pdf.setFontSize(10);
    }

    setSmall(pdf) {
      pdf.setFont(this.customFontsReady ? "Lato-Regular" : "helvetica", "normal");
      pdf.setFontSize(8);
    }
  }

  function setTextColor(pdf, tone) {
    if (tone === "secondary") {
      pdf.setTextColor(148, 148, 148);
      return;
    }
    pdf.setTextColor(74, 63, 53);
  }

  function renderHeading(pdf, cursor, text, options = {}) {
    const fontManager = options.fontManager || new FontManager({});
    const lineHeight = options.lineHeight || 7;
    const gapAfter = options.gapAfter === undefined ? 3 : options.gapAfter;
    const keepWithNext =
      options.keepWithNext === undefined ? 5 : options.keepWithNext;
    if (options.level === "subheading") fontManager.setSubheading(pdf);
    else fontManager.setTitle(pdf);
    setTextColor(pdf, "secondary");
    const lines = pdf.splitTextToSize(cleanText(text), cursor.maxWidth);
    const requiredHeight = lines.length * lineHeight + gapAfter + keepWithNext;

    cursor.ensureSpace(requiredHeight);
    if (options.level === "subheading") fontManager.setSubheading(pdf);
    else fontManager.setTitle(pdf);
    setTextColor(pdf, "secondary");

    lines.forEach((line) => {
      pdf.text(line, cursor.x, cursor.y);
      cursor.y += lineHeight;
    });
    cursor.y += gapAfter;
  }

  function getTailReserve(cursor, options, lineHeight = 0) {
    const requested = Number(options.tailReserve);
    if (!Number.isFinite(requested) || requested <= 0) return 0;
    if (options.finalKeepWithNext === false) return 0;

    const contentTop = cursor.pageManager.getContentTop(cursor.pageNumber);
    const fullBodyHeight = cursor.pageManager.contentBottom - contentTop;
    return lineHeight + requested <= fullBodyHeight ? requested : 0;
  }

  function renderParagraph(pdf, cursor, text, options = {}) {
    const fontManager = options.fontManager || new FontManager({});
    const lineHeight = options.lineHeight || 5;
    const gapAfter = options.gapAfter || 0;
    const indent = options.indent || 0;
    const maxWidth = cursor.maxWidth - indent;
    const continuationReserve =
      options.continuationReserve === undefined
        ? 10
        : options.continuationReserve;
    const tailReserve = getTailReserve(cursor, options, lineHeight);

    fontManager.setBody(pdf);
    setTextColor(pdf, "primary");
    const cleanedText = cleanText(text);
    const sentences = splitIntoSentences(cleanedText);
    if (sentences.length === 0) return;

    const lines = pdf.splitTextToSize(cleanedText, maxWidth);
    const sentenceLines = sentences.map((sentence) =>
      pdf.splitTextToSize(sentence, maxWidth)
    );
    const sentenceEndOffsets = [];
    let sentenceOffset = 0;
    sentences.forEach((sentence) => {
      sentenceOffset += sentence.length;
      sentenceEndOffsets.push(sentenceOffset);
      sentenceOffset += 1;
    });

    const lineEndOffsets = [];
    let searchOffset = 0;
    lines.forEach((line) => {
      const lineStart = cleanedText.indexOf(line, searchOffset);
      if (lineStart < 0) {
        lineEndOffsets.push(null);
        return;
      }
      const lineEnd = lineStart + line.length;
      lineEndOffsets.push(lineEnd);
      searchOffset = lineEnd;
    });

    const lineBoundaryForOffset = (offset) => {
      const lineIndex = lineEndOffsets.findIndex((lineEnd) => {
        if (lineEnd === null) return false;
        return (
          lineEnd === offset ||
          (lineEnd > offset && /^\s*$/.test(cleanedText.slice(offset, lineEnd)))
        );
      });
      return lineIndex < 0 ? null : lineIndex + 1;
    };
    const sentenceEndLines = sentenceEndOffsets.map(lineBoundaryForOffset);
    const sentenceChunks = new Map();
    sentences.forEach((sentence, index) => {
      const startLine = index === 0 ? 0 : sentenceEndLines[index - 1];
      const endLine = sentenceEndLines[index];
      if (
        Number.isInteger(startLine) &&
        Number.isInteger(endLine) &&
        endLine > startLine
      ) {
        sentenceChunks.set(startLine, {
          endLine,
          height: sentenceLines[index].length * lineHeight,
        });
      }
    });

    let lineIndex = 0;
    let drawnLineCount = 0;
    while (lineIndex < lines.length) {
      const chunk = sentenceChunks.get(lineIndex);
      const contentTop = cursor.pageManager.getContentTop(cursor.pageNumber);
      const fullBodyHeight = cursor.pageManager.contentBottom - contentTop;
      const chunkTailReserve =
        chunk && chunk.endLine === lines.length ? tailReserve : 0;
      const requiredHeight = chunk
        ? chunk.height + chunkTailReserve
        : lineHeight;
      const canMoveAsWhole =
        chunk &&
        (drawnLineCount > 0 || chunkTailReserve > 0) &&
        requiredHeight + continuationReserve <= fullBodyHeight &&
        cursor.y + requiredHeight > cursor.pageManager.contentBottom;

      if (canMoveAsWhole) {
        const changedPage = cursor.ensureSpace(requiredHeight);
        if (changedPage && typeof options.onPageBreak === "function") {
          options.onPageBreak(cursor);
        }
        if (changedPage) {
          fontManager.setBody(pdf);
          setTextColor(pdf, "primary");
        }
      }

      const endLine = chunk ? chunk.endLine : lineIndex + 1;
      while (lineIndex < endLine) {
        const isLastLine = lineIndex === lines.length - 1;
        const changedPage = cursor.ensureSpace(
          lineHeight + (isLastLine ? tailReserve : 0)
        );
        if (changedPage && typeof options.onPageBreak === "function") {
          options.onPageBreak(cursor);
        }
        if (changedPage) {
          fontManager.setBody(pdf);
          setTextColor(pdf, "primary");
        }
        pdf.text(lines[lineIndex], cursor.x + indent, cursor.y);
        cursor.y += lineHeight;
        lineIndex += 1;
        drawnLineCount += 1;
      }
    }
    cursor.y += gapAfter;
  }

  function renderList(pdf, cursor, items, options = {}) {
    const fontManager = options.fontManager || new FontManager({});
    const lineHeight = options.lineHeight || 5;
    const bulletIndent = options.bulletIndent || 4;
    const gapAfter = options.gapAfter === undefined ? 2 : options.gapAfter;
    const continuationReserve =
      options.continuationReserve === undefined
        ? 10
        : options.continuationReserve;
    const tailReserve = getTailReserve(cursor, options, lineHeight);
    let lastItemIndex = -1;
    items.forEach((item, index) => {
      if (cleanText(item)) lastItemIndex = index;
    });

    fontManager.setBody(pdf);
    setTextColor(pdf, "primary");
    items.forEach((item, itemIndex) => {
      const lines = pdf.splitTextToSize(
        cleanText(item),
        cursor.maxWidth - bulletIndent
      );
      const itemTailReserve = itemIndex === lastItemIndex ? tailReserve : 0;
      const requiredHeight = lines.length * lineHeight + itemTailReserve;
      const contentTop = cursor.pageManager.getContentTop(cursor.pageNumber);
      const fullBodyHeight = cursor.pageManager.contentBottom - contentTop;
      const canMoveAsWhole =
        itemTailReserve > 0 &&
        requiredHeight + continuationReserve <= fullBodyHeight &&
        cursor.y + requiredHeight > cursor.pageManager.contentBottom;

      if (canMoveAsWhole) {
        const changedPage = cursor.ensureSpace(requiredHeight);
        if (changedPage && typeof options.onPageBreak === "function") {
          options.onPageBreak(cursor);
        }
        if (changedPage) {
          fontManager.setBody(pdf);
          setTextColor(pdf, "primary");
        }
      }

      lines.forEach((line, index) => {
        const isLastLine =
          itemIndex === lastItemIndex && index === lines.length - 1;
        const changedPage = cursor.ensureSpace(
          lineHeight + (isLastLine ? tailReserve : 0)
        );
        if (changedPage && typeof options.onPageBreak === "function") {
          options.onPageBreak(cursor);
          fontManager.setBody(pdf);
          setTextColor(pdf, "primary");
        }
        if (index === 0) pdf.text("•", cursor.x, cursor.y);
        pdf.text(line, cursor.x + bulletIndent, cursor.y);
        cursor.y += lineHeight;
      });
    });
    cursor.y += gapAfter;
  }

  function renderImage(pdf, cursor, imageAsset, options = {}) {
    const gapAfter = options.gapAfter === undefined ? 5 : options.gapAfter;
    const maxWidth = Math.min(options.maxWidth || cursor.maxWidth, cursor.maxWidth);
    const contentTop = cursor.pageManager.getContentTop(cursor.pageNumber);
    const fullBodyHeight = cursor.pageManager.contentBottom - contentTop;
    const continuationReserve =
      typeof options.onPageBreak === "function"
        ? options.continuationReserve || 10
        : 0;
    const tailReserve = getTailReserve(cursor, options);
    const fullMaxHeight =
      fullBodyHeight - continuationReserve - gapAfter - tailReserve;
    const requestedMaxHeight = options.maxHeight
      ? Math.min(options.maxHeight, fullMaxHeight)
      : fullMaxHeight;
    const fullPageSize = fitWithin(
      imageAsset.width,
      imageAsset.height,
      maxWidth,
      requestedMaxHeight
    );
    const remainingHeight =
      cursor.pageManager.contentBottom - cursor.y - gapAfter - tailReserve;
    const minimumUsefulHeight = options.minimumUsefulHeight || 35;
    let size = fullPageSize;

    if (
      fullPageSize.height > remainingHeight &&
      remainingHeight >= minimumUsefulHeight
    ) {
      size = fitWithin(
        imageAsset.width,
        imageAsset.height,
        maxWidth,
        remainingHeight
      );
    } else {
      const requiredHeight = fullPageSize.height + gapAfter + tailReserve;
      const changedPage = cursor.ensureSpace(requiredHeight);
      if (changedPage && typeof options.onPageBreak === "function") {
        options.onPageBreak(cursor);
        cursor.ensureSpace(requiredHeight);
      }
    }
    const x = cursor.x + (cursor.maxWidth - size.width) / 2;
    pdf.addImage(
      imageAsset.dataUrl,
      imageAsset.format,
      x,
      cursor.y,
      size.width,
      size.height
    );
    cursor.y += size.height + gapAfter;
  }

  function getPageWidth(pdf) {
    return typeof pdf.internal.pageSize.getWidth === "function"
      ? pdf.internal.pageSize.getWidth()
      : pdf.internal.pageSize.width;
  }

  function getPageHeight(pdf) {
    return typeof pdf.internal.pageSize.getHeight === "function"
      ? pdf.internal.pageSize.getHeight()
      : pdf.internal.pageSize.height;
  }

  function drawPageHeader(pdf, fontManager, title, logoAsset, links = {}) {
    const pageWidth = getPageWidth(pdf);
    const pageHeight = getPageHeight(pdf);

    pdf.setFillColor(250, 250, 248);
    pdf.rect(0, 0, pageWidth, pageHeight, "F");

    const logoSize = fitWithin(
      logoAsset.width,
      logoAsset.height,
      24,
      12
    );
    const logoX = pageWidth - 10 - logoSize.width;
    const logoY = 5 + (12 - logoSize.height) / 2;
    pdf.addImage(
      logoAsset.dataUrl,
      logoAsset.format,
      logoX,
      logoY,
      logoSize.width,
      logoSize.height
    );
    if (links.origin && typeof pdf.link === "function") {
      pdf.link(logoX, logoY, logoSize.width, logoSize.height, {
        url: links.origin,
      });
    }

    fontManager.setTitle(pdf);
    setTextColor(pdf, "primary");
    const titleLines = pdf.splitTextToSize(cleanText(title), pageWidth - 70);
    const visibleLines = titleLines.slice(0, 2);
    if (titleLines.length > 2) {
      visibleLines[1] = `${visibleLines[1]}…`;
    }
    visibleLines.forEach((line, index) => {
      const y = 12 + index * 5;
      if (links.productUrl && typeof pdf.textWithLink === "function") {
        pdf.textWithLink(line, 15, y, { url: links.productUrl });
      } else {
        pdf.text(line, 15, y);
      }
    });

    const lineY = visibleLines.length > 1 ? 22 : 18;
    pdf.setDrawColor(209, 209, 209);
    pdf.setLineWidth(0.5);
    pdf.line(10, lineY, pageWidth - 10, lineY);
    return lineY + 5;
  }

  function getPageContentTop(headerContentTop, pageNumber) {
    return (
      headerContentTop +
      (pageNumber > 1 ? PDF_CONFIG.page.continuationTopOffset : 0)
    );
  }

  function drawPageFooter(pdf, fontManager) {
    const pageWidth = getPageWidth(pdf);
    const pageHeight = getPageHeight(pdf);
    const contact = `Email: ${PDF_CONFIG.email} | Phone: ${PDF_CONFIG.phone}`;
    const contactY = pageHeight - 22;
    const dividerY = pageHeight - 19;
    const addressY = [pageHeight - 13, pageHeight - 7.5];

    fontManager.setSmall(pdf);
    setTextColor(pdf, "primary");
    pdf.text(contact, (pageWidth - pdf.getTextWidth(contact)) / 2, contactY);

    pdf.setDrawColor(209, 209, 209);
    pdf.setLineWidth(0.5);
    pdf.line(10, dividerY, pageWidth - 10, dividerY);

    PDF_CONFIG.addressLines.forEach((line, index) => {
      pdf.text(line, (pageWidth - pdf.getTextWidth(line)) / 2, addressY[index]);
    });
  }

  function hasRichSectionContent(section, imageAssets = []) {
    return Boolean(
      section &&
        ((section.paragraphs && section.paragraphs.length) ||
          (section.lists && section.lists.length) ||
          imageAssets.length)
    );
  }

  function getRichSectionFinalBlockType(section, imageAssets = []) {
    const paragraphs = (section && section.paragraphs) || [];
    const lists = (section && section.lists) || [];
    const assets = imageAssets || [];
    if (assets.some(Boolean)) return "image";
    if (
      lists.some((list) =>
        (list.items || []).some((item) => cleanText(item))
      )
    ) {
      return "list";
    }
    if (paragraphs.some((paragraph) => cleanText(paragraph))) {
      return "paragraph";
    }
    return null;
  }

  function getRichSectionFinalGap(section, imageAssets = []) {
    const blockType = getRichSectionFinalBlockType(section, imageAssets);
    if (blockType === "image") return 5;
    if (blockType === "list" || blockType === "paragraph") return 2;
    return 0;
  }

  function renderRichSection(
    pdf,
    cursor,
    title,
    section,
    imageAssets,
    fontManager,
    options = {}
  ) {
    const assets = imageAssets || [];
    if (!hasRichSectionContent(section, assets)) return false;

    const finalBlockType = getRichSectionFinalBlockType(section, assets);
    const finalTextGapAfter =
      options.finalTextGapAfter === undefined ? 2 : options.finalTextGapAfter;
    const finalImageGapAfter =
      options.finalImageGapAfter === undefined ? 5 : options.finalImageGapAfter;
    const tailReserve =
      options.finalKeepWithNext === false
        ? 0
        : Math.max(0, Number(options.tailReserve) || 0);
    const paragraphs = (section && section.paragraphs) || [];
    const lists = (section && section.lists) || [];
    const lastParagraphIndex = paragraphs.reduce(
      (lastIndex, paragraph, index) =>
        cleanText(paragraph) ? index : lastIndex,
      -1
    );
    const lastListIndex = lists.reduce(
      (lastIndex, list, index) =>
        (list.items || []).some((item) => cleanText(item)) ? index : lastIndex,
      -1
    );
    const lastAssetIndex = assets.reduce(
      (lastIndex, asset, index) => (asset ? index : lastIndex),
      -1
    );

    renderHeading(pdf, cursor, title, { fontManager });
    const renderContinuation = () => {
      renderHeading(pdf, cursor, `${title} (Continued)`, {
        fontManager,
        keepWithNext: 5,
      });
    };

    paragraphs.forEach((paragraph, index) => {
      const isFinalBlock =
        finalBlockType === "paragraph" && index === lastParagraphIndex;
      renderParagraph(pdf, cursor, paragraph, {
        fontManager,
        gapAfter: isFinalBlock ? finalTextGapAfter : 2,
        finalKeepWithNext: isFinalBlock && options.finalKeepWithNext,
        tailReserve: isFinalBlock ? tailReserve : 0,
        onPageBreak: renderContinuation,
      });
    });

    lists.forEach((list, index) => {
      const isFinalBlock =
        finalBlockType === "list" && index === lastListIndex;
      renderList(pdf, cursor, list.items || [], {
        fontManager,
        gapAfter: isFinalBlock ? finalTextGapAfter : 2,
        finalKeepWithNext: isFinalBlock && options.finalKeepWithNext,
        tailReserve: isFinalBlock ? tailReserve : 0,
        onPageBreak: renderContinuation,
      });
    });

    assets.forEach((asset, index) => {
      const isFinalBlock =
        finalBlockType === "image" && index === lastAssetIndex;
      renderImage(pdf, cursor, asset, {
        maxWidth: cursor.maxWidth,
        gapAfter: isFinalBlock ? finalImageGapAfter : 5,
        finalKeepWithNext: isFinalBlock && options.finalKeepWithNext,
        tailReserve: isFinalBlock ? tailReserve : 0,
        onPageBreak: renderContinuation,
      });
    });

    return true;
  }

  function renderProductImageGrid(pdf, startY, imageAssets) {
    const assets = (imageAssets || []).filter(Boolean).slice(0, 6);
    if (assets.length === 0) return startY;

    const boxes = [
      { x: 7, y: startY, width: 96, height: 96 },
      { x: 106, y: startY, width: 96, height: 96 },
      { x: 7, y: startY + 98, width: 47, height: 47 },
      { x: 56, y: startY + 98, width: 47, height: 47 },
      { x: 106, y: startY + 98, width: 47, height: 47 },
      { x: 155, y: startY + 98, width: 47, height: 47 },
    ];

    let lowestBoxBottom = startY;
    assets.forEach((asset, index) => {
      const box = boxes[index];
      const size = fitWithin(
        asset.width,
        asset.height,
        box.width,
        box.height
      );
      const x = box.x + (box.width - size.width) / 2;
      const y = box.y + (box.height - size.height) / 2;
      pdf.addImage(asset.dataUrl, asset.format, x, y, size.width, size.height);
      lowestBoxBottom = Math.max(lowestBoxBottom, box.y + box.height);
    });

    return lowestBoxBottom + 15;
  }

  function renderFirstGroup(
    pdf,
    pageManager,
    snapshot,
    assets,
    fontManager,
    startY
  ) {
    const left = new ColumnCursor(pageManager, {
      x: PDF_CONFIG.page.leftColumnX,
      maxWidth: PDF_CONFIG.page.columnWidth,
      pageNumber: 1,
      y: startY,
    });
    const right = new ColumnCursor(pageManager, {
      x: PDF_CONFIG.page.rightColumnX,
      maxWidth: PDF_CONFIG.page.columnWidth,
      pageNumber: 1,
      y: startY,
    });

    renderParagraph(pdf, left, "Brand: Vinlighting", {
      fontManager,
      gapAfter: 2,
    });
    renderParagraph(
      pdf,
      left,
      `Availability: ${PDF_CONFIG.availability}`,
      { fontManager, gapAfter: 3 }
    );
    renderHeading(pdf, left, "Shipping Options", {
      fontManager,
      level: "subheading",
      gapAfter: 2,
    });
    PDF_CONFIG.shippingOptions.forEach((option) => {
      renderParagraph(pdf, left, option, { fontManager });
    });
    left.y += 8;

    renderRichSection(
      pdf,
      left,
      "Description",
      snapshot.description,
      assets.descriptionImages || [],
      fontManager
    );

    renderHeading(pdf, right, "Product Options", { fontManager });
    if (!snapshot.variants || snapshot.variants.length === 0) {
      renderParagraph(pdf, right, "Standard Configuration", {
        fontManager,
        indent: 5,
      });
    } else {
      snapshot.variants.forEach((group) => {
        fontManager.setSubheading(pdf);
        const groupHeadingLines = pdf.splitTextToSize(
          cleanText(group.subheading),
          right.maxWidth
        );
        const groupHeadingHeight = groupHeadingLines.length * 7 + 2 + 5;
        const groupMovedToNewPage = right.ensureSpace(groupHeadingHeight);
        if (groupMovedToNewPage) {
          renderHeading(pdf, right, "Product Options (Continued)", {
            fontManager,
          });
        }
        renderHeading(pdf, right, group.subheading, {
          fontManager,
          level: "subheading",
          gapAfter: 2,
        });
        group.options.forEach((option) => {
          renderParagraph(pdf, right, option, {
            fontManager,
            indent: 5,
            onPageBreak: () => {
              renderHeading(pdf, right, "Product Options (Continued)", {
                fontManager,
              });
              renderHeading(pdf, right, group.subheading, {
                fontManager,
                level: "subheading",
                gapAfter: 2,
              });
            },
          });
        });
        right.y += 3;
      });
    }

    return { left, right };
  }

  function prepareDetailsCursor(pageManager, cursor, minimumPage = 2) {
    if (cursor.pageNumber < minimumPage) {
      cursor.pageNumber = minimumPage;
      cursor.y = pageManager.ensurePage(minimumPage);
    } else {
      pageManager.ensurePage(cursor.pageNumber);
    }
    return cursor;
  }

  function getImageRenderReserve(pageManager, cursor, imageAsset, options = {}) {
    const gapAfter = options.gapAfter === undefined ? 5 : options.gapAfter;
    const maxWidth = Math.min(options.maxWidth || cursor.maxWidth, cursor.maxWidth);
    const contentTop = pageManager.getContentTop(cursor.pageNumber);
    const fullBodyHeight = pageManager.contentBottom - contentTop;
    const size = fitWithin(
      imageAsset.width,
      imageAsset.height,
      maxWidth,
      fullBodyHeight - gapAfter
    );
    return size.height + gapAfter;
  }

  function renderSecondGroup(
    pdf,
    pageManager,
    snapshot,
    assets,
    fontManager,
    firstGroupCursors
  ) {
    const firstPageTop = pageManager.ensurePage(1);
    const left = firstGroupCursors
      ? firstGroupCursors.left
      : new ColumnCursor(pageManager, {
          x: PDF_CONFIG.page.leftColumnX,
          maxWidth: PDF_CONFIG.page.columnWidth,
          pageNumber: 1,
          y: firstPageTop,
        });
    const right = firstGroupCursors
      ? firstGroupCursors.right
      : new ColumnCursor(pageManager, {
          x: PDF_CONFIG.page.rightColumnX,
          maxWidth: PDF_CONFIG.page.columnWidth,
          pageNumber: 1,
          y: firstPageTop,
    });
    prepareDetailsCursor(pageManager, left);
    prepareDetailsCursor(pageManager, right);
    const startPage = Math.min(left.pageNumber, right.pageNumber);
    const leftHasFirstGroupContent =
      left.y > pageManager.getContentTop(left.pageNumber);
    const rightHasFirstGroupContent =
      right.y > pageManager.getContentTop(right.pageNumber);

    const certificateReserve = getImageRenderReserve(
      pageManager,
      left,
      assets.certificate,
      { maxWidth: 80, gapAfter: 5 }
    );
    const specificationsHaveContent = hasRichSectionContent(
      snapshot.specifications,
      assets.specificationImages || []
    );
    const keyFeaturesHaveContent = hasRichSectionContent(
      snapshot.keyFeatures,
      assets.keyFeatureImages || []
    );
    const descriptionHaveContent = hasRichSectionContent(
      snapshot.description,
      assets.descriptionImages || []
    );
    const dimensionsHaveContent = hasRichSectionContent(
      snapshot.dimensions,
      assets.dimensionImages || []
    );
    if (
      descriptionHaveContent &&
      leftHasFirstGroupContent &&
      (keyFeaturesHaveContent || specificationsHaveContent)
    ) {
      left.y += PDF_CONFIG.sectionSpacing.descriptionToDetails;
    }
    if (rightHasFirstGroupContent && dimensionsHaveContent) {
      right.y += PDF_CONFIG.sectionSpacing.productOptionsToDimensions;
    }
    const keyFeaturesTailReserve = keyFeaturesHaveContent && !specificationsHaveContent
      ? certificateReserve +
        5 +
        getRichSectionFinalGap(
          snapshot.keyFeatures,
          assets.keyFeatureImages || []
        )
      : 0;

    const renderedKeyFeatures = renderRichSection(
      pdf,
      left,
      "Key Features",
      snapshot.keyFeatures,
      assets.keyFeatureImages || [],
      fontManager,
      keyFeaturesTailReserve > 0
        ? { finalKeepWithNext: true, tailReserve: keyFeaturesTailReserve }
        : undefined
    );
    if (renderedKeyFeatures) left.y += 5;
    if (renderedKeyFeatures && specificationsHaveContent) {
      left.y += PDF_CONFIG.sectionSpacing.keyFeaturesToSpecifications;
    }

    renderRichSection(
      pdf,
      left,
      "Specifications",
      snapshot.specifications,
      assets.specificationImages || [],
      fontManager,
      {
        finalTextGapAfter: 0,
        finalImageGapAfter: 2,
        finalKeepWithNext: specificationsHaveContent,
        tailReserve: specificationsHaveContent ? certificateReserve : 0,
      }
    );
    renderImage(pdf, left, assets.certificate, {
      maxWidth: 80,
      gapAfter: 5,
    });

    renderRichSection(
      pdf,
      right,
      "Dimensions",
      snapshot.dimensions,
      assets.dimensionImages || [],
      fontManager
    );

    return { startPage, left, right };
  }

  function loadFontScript(rootRef) {
    if (
      rootRef.PlayfairDisplayRegularBase64 &&
      rootRef.LatoRegularBase64
    ) {
      return Promise.resolve(true);
    }
    if (fontScriptPromise) return fontScriptPromise;

    const documentRef = rootRef.document;
    if (!documentRef || !documentRef.createElement || !documentRef.head) {
      return Promise.reject(new Error("Font loading is unavailable."));
    }

    fontScriptPromise = new Promise((resolve, reject) => {
      const script = documentRef.createElement("script");
      const assetScript = documentRef.querySelector(
        'script[src*="/cdn/shop/"]'
      );

      if (assetScript && assetScript.src) {
        const fontUrl = new URL(assetScript.src, rootRef.location && rootRef.location.href);
        fontUrl.pathname = fontUrl.pathname.replace(
          /\/[^/]*$/,
          "/pdf-fonts-data.js"
        );
        fontUrl.search = "";
        script.src = fontUrl.toString();
      } else {
        script.src = "pdf-fonts-data.js";
      }

      script.onload = () => {
        if (
          rootRef.PlayfairDisplayRegularBase64 &&
          rootRef.LatoRegularBase64
        ) {
          resolve(true);
          return;
        }
        reject(
          new Error(
            "The font file loaded, but one or more font definitions are missing."
          )
        );
      };
      script.onerror = () => reject(new Error("The font file could not be loaded."));
      documentRef.head.appendChild(script);
    }).catch((error) => {
      fontScriptPromise = null;
      throw error;
    });

    return fontScriptPromise;
  }

  async function loadPdfAssets(snapshot, rootRef, dependencies = {}) {
    const converter = dependencies.converter || convertImageToDataUrl;
    const logger = dependencies.logger || rootRef.console || console;
    const environment = {
      ImageCtor: rootRef.Image,
      documentRef: rootRef.document,
    };

    const loadRequired = async (url, message) => {
      try {
        return await converter(
          url,
          {
            keepPng: true,
            maxWidth: 1000,
            maxHeight: 1200,
            timeoutMs: PDF_CONFIG.imageTimeoutMs,
          },
          environment
        );
      } catch (error) {
        throw new Error(message);
      }
    };

    const [logo, certificate] = await Promise.all([
      loadRequired(
        PDF_CONFIG.logoUrl,
        "The Vinlighting logo could not be loaded. Please try again."
      ),
      loadRequired(
        PDF_CONFIG.certificateUrl,
        "The certification image could not be loaded. Please try again."
      ),
    ]);

    const loadOptionalGroup = async (urls) => {
      const loaded = await mapWithConcurrency(
        (urls || []).filter(Boolean),
        PDF_CONFIG.imageConcurrency,
        async (url) => {
          try {
            return await converter(
              url,
              {
                keepPng: false,
                maxWidth: 800,
                maxHeight: 1200,
                timeoutMs: PDF_CONFIG.imageTimeoutMs,
              },
              environment
            );
          } catch (error) {
            if (logger && typeof logger.warn === "function") {
              logger.warn(`Optional PDF image skipped: ${url}`);
            }
            return null;
          }
        }
      );
      return loaded.filter(Boolean);
    };

    return {
      logo,
      certificate,
      productImages: await loadOptionalGroup((snapshot.images || []).slice(0, 6)),
      descriptionImages: await loadOptionalGroup(snapshot.description.images),
      keyFeatureImages: await loadOptionalGroup(snapshot.keyFeatures.images),
      specificationImages: await loadOptionalGroup(snapshot.specifications.images),
      dimensionImages: await loadOptionalGroup(snapshot.dimensions.images),
    };
  }

  function generatePdfDocument(rootRef, snapshot, assets) {
    if (
      !rootRef.jspdf ||
      typeof rootRef.jspdf.jsPDF !== "function"
    ) {
      throw new Error("The PDF library is unavailable.");
    }

    const pdf = new rootRef.jspdf.jsPDF({
      compress: true,
      unit: "mm",
      format: "a4",
    });
    const fontManager = new FontManager(rootRef);
    fontManager.register(pdf);

    const locationRef = rootRef.location || {};
    const pageManager = new PageManager(pdf, {
      contentBottom: PDF_CONFIG.page.contentBottom,
      decoratePage: (pageNumber) =>
        getPageContentTop(
          drawPageHeader(
            pdf,
            fontManager,
            snapshot.title,
            assets.logo,
            {
              productUrl: locationRef.href || "",
              origin: locationRef.origin || "",
            }
          ),
          pageNumber
        ),
      drawFooter: () => drawPageFooter(pdf, fontManager),
    });

    const contentTop = pageManager.initializeFirstPage();
    const firstGroupStart = renderProductImageGrid(
      pdf,
      contentTop,
      assets.productImages
    );
    const firstGroupCursors = renderFirstGroup(
      pdf,
      pageManager,
      snapshot,
      assets,
      fontManager,
      firstGroupStart
    );
    renderSecondGroup(
      pdf,
      pageManager,
      snapshot,
      assets,
      fontManager,
      firstGroupCursors
    );
    pageManager.finalizeFooters();
    return pdf;
  }

  async function generateAndSavePdf(rootRef, updateProgress, dependencies = {}) {
    if (
      !rootRef.jspdf ||
      typeof rootRef.jspdf.jsPDF !== "function"
    ) {
      throw new Error("The PDF library is unavailable.");
    }

    const collect = dependencies.collectProductSnapshot || collectProductSnapshot;
    const loadAssets = dependencies.loadPdfAssets || loadPdfAssets;
    const generate = dependencies.generatePdfDocument || generatePdfDocument;
    const logger = dependencies.logger || rootRef.console || console;
    const snapshot = collect(rootRef.document);
    updateProgress(25);

    try {
      await withTimeout(
        loadFontScript(rootRef),
        PDF_CONFIG.imageTimeoutMs,
        "Font loading timed out."
      );
    } catch (error) {
      if (logger && typeof logger.warn === "function") {
        logger.warn("Custom PDF fonts are unavailable; built-in fonts will be used.");
      }
    }

    updateProgress(45);
    const assets = await loadAssets(snapshot, rootRef, { logger });
    updateProgress(70);
    const pdf = generate(rootRef, snapshot, assets);
    updateProgress(90);
    pdf.save(`${sanitizeFileName(snapshot.title)} - Vinlighting.pdf`);
    updateProgress(100);
    return pdf;
  }

  function formatGenerationError(error) {
    const detail = error && error.message
      ? error.message
      : "An unexpected error occurred. Please try again.";
    if (detail.startsWith("We couldn't generate the PDF.")) return detail;
    return `We couldn't generate the PDF. ${detail}`;
  }

  function initialize(rootRef, dependencies = {}) {
    const documentRef = rootRef && rootRef.document;
    if (!documentRef) return false;

    const button = documentRef.getElementById(PDF_CONFIG.buttonId);
    if (!button) return false;
    if (initializedButtons.has(button)) return true;

    const generator = dependencies.generateAndSavePdf || generateAndSavePdf;
    let generating = false;
    button.addEventListener("click", async (event) => {
      if (event && typeof event.preventDefault === "function") {
        event.preventDefault();
      }
      if (generating) return;

      generating = true;
      const originalHtml = button.innerHTML;
      const originalDisabled = button.disabled;
      const status =
        typeof button.querySelector === "function"
          ? button.querySelector("[data-pdf-status]")
          : null;
      const originalStatusText = status ? status.textContent : originalHtml;
      button.disabled = true;

      const updateProgress = (percent) => {
        const progressText = `Generating PDF... ${percent}%`;
        if (status) status.textContent = progressText;
        else button.innerHTML = `<span>${progressText}</span>`;
      };

      try {
        updateProgress(10);
        await generator(rootRef, updateProgress);
      } catch (error) {
        const message = formatGenerationError(error);
        if (typeof rootRef.alert === "function") rootRef.alert(message);
        else if (rootRef.console && typeof rootRef.console.error === "function") {
          rootRef.console.error(message);
        }
      } finally {
        if (status) status.textContent = originalStatusText;
        else button.innerHTML = originalHtml;
        button.disabled = originalDisabled;
        generating = false;
      }
    });

    initializedButtons.add(button);
    return true;
  }

  function boot(rootRef) {
    if (!rootRef || !rootRef.document) return false;
    if (rootRef.document.readyState === "loading") {
      rootRef.document.addEventListener(
        "DOMContentLoaded",
        () => initialize(rootRef),
        { once: true }
      );
      return true;
    }
    return initialize(rootRef);
  }

  return {
    boot,
    ColumnCursor,
    FontManager,
    PDF_CONFIG,
    PageManager,
    cleanText,
    collectProductSnapshot,
    convertImageToDataUrl,
    drawPageFooter,
    drawPageHeader,
    extractVariantGroups,
    extractVariantGroupsFromProduct,
    findCollapsibleSection,
    fitWithin,
    formatGenerationError,
    generateAndSavePdf,
    generatePdfDocument,
    getPageContentTop,
    getShopifyImageUrl,
    getVariantGroups,
    mapWithConcurrency,
    mergeVariantGroups,
    readProductJson,
    readRichSection,
    renderFirstGroup,
    renderHeading,
    renderImage,
    renderList,
    renderParagraph,
    renderProductImageGrid,
    renderRichSection,
    renderSecondGroup,
    prepareDetailsCursor,
    splitIntoSentences,
    initialize,
    loadFontScript,
    loadPdfAssets,
    sanitizeFileName,
    withTimeout,
  };
});
